import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface FiltroTipo {
  label: string;
  options: string[];
}

export interface HomepageConfig {
  id: number;
  hero_title: string;
  hero_subtitle: string;
  hero_slides: string[];
  hero_institutional_text: string;
  hero_cta_text: string;
  hero_cta_link: string;
  que_es_title: string;
  que_es_image: string;
  que_es_banner_text: string;
  que_es_left_text: string;
  que_es_right_text: string;
  que_es_button_text: string;
  que_es_button_link: string;
  actualidad_title: string;
  territorio_title: string;
  voces_title: string;
  timeline_title: string;
  filtros_config: FiltroTipo[];
  datos_territorio_visible: boolean;
  inicio_territorio_visible: boolean;
}

const defaultFiltrosConfig: FiltroTipo[] = [
  { label: "Segmento", options: ["Inauguración", "Extensión", "Avances", "Actividades", "Alianzas"] },
  { label: "Comuna", options: ["Iquique", "Alto Hospicio", "Pica", "Pozo Almonte", "Huara", "Camiña", "Colchane"] },
  { label: "Fecha", options: ["2026", "2025", "2024"] },
];

const defaultConfig: HomepageConfig = {
  id: 1,
  hero_title:
    "Transferencia Brechas y Competencias Capital Humano Médico en el Área de la Salud",
  hero_subtitle: "",
  hero_slides: [
    "https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/1c3df1a8-fbeb-467a-a1a4-2051fc16c0d1_02.png?v=286519a9ca59e07b89846d5d13ec3dad",
    "https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/2a226ddc-d454-4f80-8fa4-d4df7f221fb8_compressed_09.webp",
    "https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/57a05ae3-ae3d-4c15-9eae-16d46723c477_07.png?v=e8557c2d0170829d60ecc2f09546ee6e",
  ],
  hero_institutional_text:
    "Proyecto financiado por el Gobierno Regional de Tarapacá y su Consejo Regional a través del Fondo Regional para la Productividad y Desarrollo (FRPD).",
  hero_cta_text: "Conoce más",
  hero_cta_link: "#actualidad",
  que_es_title: "¿Qué es MásMed Tarapacá?",
  que_es_image:
    "https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/b13204d283ee98c13ee037fb29b56ccf.png",
  que_es_banner_text: "Más especialistas. Más salud. Mejor vida.",
  que_es_left_text:
    "MásMed Tarapacá es un proyecto orientado al estudio de las brechas y necesidades de especialistas médicos en la Región de Tarapacá. La iniciativa busca comprender cómo las diferencias territoriales, el acceso a la salud y la disponibilidad de profesionales impactan la calidad de vida de las personas, especialmente en una región diversa y extensa como Tarapacá. A través del levantamiento y análisis de información basada en evidencia, MásMed busca aportar conocimiento útil para fortalecer la toma de decisiones en salud pública y contribuir al desarrollo sanitario regional desde una mirada territorial y colaborativa.",
  que_es_right_text:
    "MásMed Tarapacá es un proyecto ejecutado por la Universidad Arturo Prat y financiado por el Gobierno Regional de Tarapacá, con apoyo del Consejo Regional, a través del Fondo Regional para la Productividad y el Desarrollo (FRPD), en el marco del Concurso de Innovación, Competitividad, Ciencia y Tecnología (CICYT).",
  que_es_button_text: "Ver más",
  que_es_button_link: "/masmed",
  actualidad_title: "Actualidad",
  territorio_title: "Territorio",
  voces_title: "Voces que construyen salud",
  timeline_title: "Línea de tiempo",
  filtros_config: defaultFiltrosConfig,
  datos_territorio_visible: true,
  inicio_territorio_visible: true,
};

function mapRow(row: any): HomepageConfig {
  return {
    id: row.id,
    hero_title: row.hero_title ?? defaultConfig.hero_title,
    hero_subtitle: row.hero_subtitle ?? "",
    hero_slides: Array.isArray(row.hero_slides)
      ? row.hero_slides
      : defaultConfig.hero_slides,
    hero_institutional_text:
      row.hero_institutional_text ?? defaultConfig.hero_institutional_text,
    hero_cta_text: row.hero_cta_text ?? defaultConfig.hero_cta_text,
    hero_cta_link: row.hero_cta_link ?? defaultConfig.hero_cta_link,
    que_es_title: row.que_es_title ?? defaultConfig.que_es_title,
    que_es_image: row.que_es_image ?? defaultConfig.que_es_image,
    que_es_banner_text:
      row.que_es_banner_text ?? defaultConfig.que_es_banner_text,
    que_es_left_text: row.que_es_left_text ?? defaultConfig.que_es_left_text,
    que_es_right_text:
      row.que_es_right_text ?? defaultConfig.que_es_right_text,
    que_es_button_text:
      row.que_es_button_text ?? defaultConfig.que_es_button_text,
    que_es_button_link:
      row.que_es_button_link ?? defaultConfig.que_es_button_link,
    actualidad_title: row.actualidad_title ?? defaultConfig.actualidad_title,
    territorio_title: row.territorio_title ?? defaultConfig.territorio_title,
    voces_title: row.voces_title ?? defaultConfig.voces_title,
    timeline_title: row.timeline_title ?? defaultConfig.timeline_title,
    filtros_config: Array.isArray(row.filtros_config)
      ? row.filtros_config
      : defaultConfig.filtros_config,
    datos_territorio_visible: row.datos_territorio_visible ?? true,
    inicio_territorio_visible: row.inicio_territorio_visible ?? true,
  };
}

export function useHomepageConfig() {
  const [data, setData] = useState<HomepageConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from("homepage_config")
        .select("*")
        .eq("id", 1)
        .maybeSingle();

      if (err) throw err;
      if (rows) {
        setData(mapRow(rows));
      }
    } catch (e: any) {
      setError(e.message || "Error cargando configuración del inicio");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return { data, loading, error, refetch: fetchConfig };
}